export const POST_LOGIN_REDIRECT_STORAGE_KEY = 'plm.postLoginRedirect';

const REDIRECT_PARAM = 'redirect';

// Entra redirects back to the fixed /redirect URI, so these would send the user nowhere useful
const EXCLUDED_PATHS = ['/login', '/redirect'];

// Far above real app paths. Limits the input from a crafted redirect param, because some path handling
// (e.g. React Router's trailing slash regex) takes super-linear time on long inputs and could freeze the tab.
const MAX_PATH_LENGTH = 2048;

/**
 * Checks whether a path is safe to navigate to after login.
 * The path is parsed the way the browser will read it, because inputs like "//host", "/\host" or "/\t/host"
 * look like app paths but point to another host. A parsed path like "//host" (e.g. from "/..//host") is rejected
 * as well, so safety does not depend on the router collapsing double slashes.
 *
 * @param {unknown} path Candidate path.
 * @returns {boolean} True when the path is a same-origin app path other than the login or redirect page.
 */
export const isValidPostLoginPath = (path: unknown): path is string => {
  if (typeof path !== 'string' || !path.startsWith('/') || path.length > MAX_PATH_LENGTH) {
    return false;
  }
  try {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname.startsWith('//')) {
      return false;
    }
    // The router matches paths decoded, case-insensitively and without a trailing slash
    let decodedPathname = url.pathname;
    try {
      decodedPathname = decodeURIComponent(url.pathname);
    } catch {
      // A bare "%" (e.g. "/templates/50%off") cannot be decoded. React Router falls back to the raw path in this case too.
    }
    // A loop instead of /\/+$/, which backtracks quadratically on long slash runs from a crafted redirect param
    // and marked by Sonarqube as a potential denial of service vulnerability.
    let end = decodedPathname.length;
    while (end > 0 && decodedPathname[end - 1] === '/') {
      end--;
    }
    const pathname = decodedPathname.slice(0, end).toLowerCase();
    return !EXCLUDED_PATHS.includes(pathname);
  } catch {
    return false;
  }
};

/**
 * Builds the login URL with the requested path as a query param, so the link can also be shared.
 *
 * @param {string} targetPath Path to open after login.
 * @returns {string} The login URL.
 */
export const getLoginUrl = (targetPath: string): string =>
  isValidPostLoginPath(targetPath) ? `/login?${new URLSearchParams({ [REDIRECT_PARAM]: targetPath })}` : '/login';

/**
 * Reads the requested path from the login URL query.
 *
 * @param {string} search Query string of the login URL.
 * @returns {string | null} The requested path, or null when missing or invalid.
 */
export const getPathFromLoginSearch = (search: string): string | null => {
  const path = new URLSearchParams(search).get(REDIRECT_PARAM);
  return isValidPostLoginPath(path) ? path : null;
};

/**
 * Stores the path to open after login, or removes it when null.
 * Stored in sessionStorage because Entra ID always returns to the fixed /redirect URI, which drops the query param.
 *
 * @param {string | null} path Path to open after login.
 */
export const savePostLoginPath = (path: string | null) => {
  try {
    if (path) {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, path);
    } else {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    }
  } catch {
    // Storage can be unavailable (e.g. blocked site data). The user then lands on the default page.
  }
};

/**
 * Reads the stored path without removing it.
 *
 * @returns {string | null} The stored path, or null when missing or invalid.
 */
export const peekPostLoginPath = (): string | null => {
  try {
    const path = sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    return isValidPostLoginPath(path) ? path : null;
  } catch {
    return null;
  }
};

/**
 * Removes the stored path.
 */
export const clearPostLoginPath = () => {
  savePostLoginPath(null);
};
